import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { ProductionOrder, UpdateOrderPayload, PaginatedResponse, ProductionOrderFilters, MaterialAvailabilityResult, AvailabilityItem } from '../types/production.types';

const API_URL = '/api/production-orders';

// Hook para OBTENER todas las órdenes autorizadas para el tablero
export const useGetPlannableOrders = () => {
  return useQuery<ProductionOrder[]>({
    queryKey: ['plannableOrders'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      // Pedimos todas las órdenes AUTORIZADAS que no estén terminadas
      const { data } = await axios.get<PaginatedResponse<ProductionOrder>>(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: {
          status: 'AUTHORIZED', // Solo planeamos las autorizadas
          limit: 1000, // Traer todas para el tablero
        },
      });
      // Filtrar órdenes que no están terminadas
      return data.data.filter(order => !order.actualEndDate);
    },
    staleTime: 30 * 1000, // 30 seconds — keeps board fresh after order completion
    refetchOnWindowFocus: true,
  });
};

// Hook para OBTENER todas las órdenes con filtros (para la lista maestra)
export const useGetAllOrders = (filters: ProductionOrderFilters) => {
  return useQuery({
    queryKey: ['allOrders', filters],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get<PaginatedResponse<ProductionOrder>>(API_URL, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        params: filters,
      });
      return data;
    },
    staleTime: 2 * 60 * 1000, // Cache de 2 minutos
  });
};

// Hook para ACTUALIZAR una orden (usado por Drag-and-Drop)
export const useUpdateOrderPlanning = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ orderId, payload }: { orderId: string; payload: UpdateOrderPayload }) => {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(`${API_URL}/${orderId}`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return data;
    },
    onSuccess: () => {
      // Invalidar el cache y forzar refetch inmediato
      queryClient.invalidateQueries({ 
        queryKey: ['plannableOrders'],
        refetchType: 'active' 
      });
      queryClient.refetchQueries({ 
        queryKey: ['plannableOrders'],
        type: 'active'
      });
      queryClient.invalidateQueries({ queryKey: ['allOrders'] });
    },
    // Optimistic Updates (opcional)
    onMutate: async ({ orderId, payload }) => {
      // Cancelar queries pendientes
      await queryClient.cancelQueries({ queryKey: ['plannableOrders'] });

      // Snapshot del estado anterior
      const previousOrders = queryClient.getQueryData<ProductionOrder[]>(['plannableOrders']);

      // Actualizar optimísticamente
      if (previousOrders) {
        queryClient.setQueryData<ProductionOrder[]>(
          ['plannableOrders'],
          previousOrders.map(order =>
            order.id === orderId ? { ...order, ...payload } : order
          )
        );
      }

      // Retornar contexto para rollback si falla
      return { previousOrders };
    },
    onError: (err: any, _variables, context) => {
      // Rollback en caso de error
      if (context?.previousOrders) {
        queryClient.setQueryData(['plannableOrders'], context.previousOrders);
      }
      // The server refuses a machine narrower than the order's substrate; say why instead of silently reverting
      if (err?.response?.status === 409 && err.response.data?.code === 'WIDTH_EXCEEDED') {
        alert(err.response.data.error);
      }
    },
  });
};

// Hook para consultar la disponibilidad de materiales de una orden en Comercia (a demanda)
export const useCheckMaterialAvailability = () => {
  return useMutation({
    mutationFn: async (orderId: string): Promise<MaterialAvailabilityResult> => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get<MaterialAvailabilityResult>(`${API_URL}/${orderId}/material-availability`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      return data;
    },
  });
};

// Hook para registrar la nota de justificación al continuar con material incompleto
export const useLogMaterialShortageOverride = () => {
  return useMutation({
    mutationFn: async ({ orderId, note, items }: { orderId: string; note: string; items: AvailabilityItem[] }) => {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/${orderId}/material-shortage-override`, { note, items }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return data;
    },
  });
};

// Hook para sincronizar el flag Despachado con Comercia tras mover en el tablero
export const useSyncDespachado = () => {
  return useMutation({
    mutationFn: async ({ orderId, despachado }: { orderId: string; despachado: boolean }) => {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_URL}/${orderId}/sync-despachado`,
        { despachado },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } },
      );
      return data;
    },
  });
};

// Hook auxiliar para obtener las máquinas únicas de las órdenes
export const useGetMachines = () => {
  const { data: orders } = useGetPlannableOrders();
  
  if (!orders) return [];
  
  // Extraer máquinas únicas (excluyendo null)
  const machines = Array.from(
    new Set(
      orders
        .filter(order => order.machineName)
        .map(order => order.machineName as string)
    )
  ).sort();
  
  return machines;
};

// Tipos para máquinas/equipos (usa Catálogos -> Equipos)
export interface Machine {
  id: number;
  tenantId: number;
  code: string;
  name: string;
  description?: string;
  location?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMachinePayload {
  code: string;
  name: string;
  description?: string;
  location?: string;
}

// Hook para OBTENER todas las máquinas/equipos activos (usa /api/catalogs/equipment)
export const useGetAllMachines = () => {
  return useQuery<Machine[]>({
    queryKey: ['machines', 'catalog-equipment'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get<Machine[]>('/api/catalogs/equipment', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      return data;
    },
    staleTime: 5 * 60 * 1000, // Cache de 5 minutos
    refetchOnWindowFocus: true,
  });
};

// Hook para CREAR una máquina/equipo nuevo (usa /api/catalogs/equipment)
export const useCreateMachine = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (payload: CreateMachinePayload) => {
      const token = localStorage.getItem('token');
      const { data } = await axios.post<Machine>('/api/catalogs/equipment', payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return data;
    },
    onSuccess: () => {
      // Invalidar el cache de equipos para que se actualice la lista
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      queryClient.invalidateQueries({ queryKey: ['catalog-equipment'] });
      queryClient.refetchQueries({ queryKey: ['machines'] });
    },
  });
};
